import React from 'react';
import {
	FormattedMessage,
} from 'react-intl';

import type CK from '~/types/contentking';

import AlertsLink from '~/components/app/AlertsLink';
import CurrentPagesOverviewLink from '~/components/app/CurrentPagesOverviewLink';
import DashboardLink from '~/components/app/DashboardLink';
import IssuesOverviewLink from '~/components/app/IssuesOverviewLink';
import PlatformLink from '~/components/app/PlatformLink';
import WebsitesNavToggle from '~/components/app/WebsitesNavToggle';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';

import {
	type messages
} from '../MainNavigation';


// Re-export messages type for convenience
export type { messages };

type Props = {
	hideWebsitesToggle?: boolean,
	isTechnicalExpanded: boolean,
	messages: typeof messages,
	websiteAccountName: string | null,
	websiteId: CK.WebsiteId | null,
	websitesNavToggleRef: React.RefObject<HTMLLIElement>,
};

/**
 * Build Technical section navigation items
 *
 * Returns an array of JSX elements for the Technical section:
 * - Website Selector
 * - Dashboard
 * - Pages
 * - Platform
 * - Issues
 * - Alerts
 */
export function buildTechnicalSectionItems(props: Props): Array<React.ReactElement | null> {
	const {
		hideWebsitesToggle = false,
		isTechnicalExpanded,
		messages,
		websiteAccountName,
		websiteId,
		websitesNavToggleRef,
	} = props;

	const websiteSelectorItem = hideWebsitesToggle
		? null
		: (
			<li
				className="main-nav__item"
				data-websites-nav-toggle="true"
				key="website-selector-item"
				ref={websitesNavToggleRef}
			>
				<WebsitesNavToggle
					accountName={websiteAccountName}
					isTechnicalExpanded={isTechnicalExpanded}
				/>
			</li>
		);

	return [
		// Website Selector - hidden when account has 0 websites
		websiteSelectorItem,
		// Dashboard (text only, no icon per clickdummy)
		<li
			className="main-nav__item"
			key="technical-dashboard-item"
		>
			<DashboardLink
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.dashboard} />
							)}
						/>
					);
				}}
			</DashboardLink>
		</li>,
		// Pages (text only, no icon per clickdummy)
		<li
			className="main-nav__item"
			key="technical-pages-item"
		>
			<CurrentPagesOverviewLink>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.pages} />
							)}
						/>
					);
				}}
			</CurrentPagesOverviewLink>
		</li>,
		// Platform (text only, no icon per clickdummy)
		<li
			className="main-nav__item"
			key="technical-platform-item"
		>
			<PlatformLink
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.platform} />
							)}
						/>
					);
				}}
			</PlatformLink>
		</li>,
		// Issues (text only, no icon per clickdummy)
		<li
			className="main-nav__item"
			key="technical-issues-item"
		>
			<IssuesOverviewLink
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.issues} />
							)}
						/>
					);
				}}
			</IssuesOverviewLink>
		</li>,
		// Alerts (text only, no icon per clickdummy)
		<li
			className="main-nav__item"
			key="technical-alerts-item"
		>
			<AlertsLink
				keepSettings={false}
				websiteId={websiteId}
			>
				{({ isActive, isDimmed }) => {
					return (
						<MenuItemLayout
							icon={<></>}
							isActive={isActive}
							isDimmed={isDimmed}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.alerts} />
							)}
						/>
					);
				}}
			</AlertsLink>
		</li>,
	];
}
